import * as z from 'zod';

import { zodDateOrString } from '../../utils';

/**
 * #### Fannie Mae Delinquency Status Code File
 *
 * This report is a 76-character fixed length string. Character lengths and positions are noted at the individual fields.
 *
 * From Fannie's docs: (https://servicing-guide.fanniemae.com/svc/f-1-21/reporting-delinquent-mortgage-loan-fannie-maes-servicing-solutions-system?utm_source=openai)
 *
 * The servicer must report delinquency status information to Fannie Mae through Fannie Mae’s
 * servicing solutions system in accordance with D2-4-01, Reporting a Delinquent Mortgage Loan to Fannie Mae.
 *
 * By the second business day of each month, the servicer must report delinquency status information as part
 * of its next scheduled delinquency status report for any mortgage loan, including regular servicing option
 * MBS mortgage loans, that was either
 *  - 30 or more days delinquent as of the last day of the preceding month; or
 *  - for which an action was taken to manage the delinquency (such as granting forbearance, agreeing to a
 *    mortgage loan modification, filing for bankruptcy, etc.) during the preceding month, even if the mortgage
 *    loan was current or less than 30 days delinquent.
 */

const FannieMaeDelinquencyStatusCodes = z.enum([
  // Priority Level 1 (Approved workout options)
  'BF', // Trial Modification
  '09', // Forbearance
  '12', // Repayment Plan
  '17', // Short Sale Approved/Offer Received
  '27', // Assumption
  '28', // Modification
  '29', // Charge‑off
  '32', // Military Indulgence
  '44', // Mortgage Release
  // Priority Level 2
  'H5', // Complete Borrower Response Package (one month only)
  // Priority Level 3 (Bankruptcy)
  '3L', // Ch. 7 Asset Case
  '3M', // Bankruptcy–Property Surrendered
  '59', // Ch. 12
  '65', // Ch. 7
  '66', // Ch. 11
  '67', // Ch. 13
  '69', // Ch. 13 Plan Confirmed
  // Priority Level 4 (Foreclosure/legal)
  '20', // Reinstatement
  '24', // Drug Seizure
  '30', // Third‑Party Sale
  '31', // Probate
  '33', // Contested/Litigated Foreclosure
  '43', // Foreclosure
  '61', // Second Lien Considerations
  '63', // Veterans Affairs—Refund
  '71', // Foreclosure Sale Scheduled
  '94', // Judgment or Decree Entered
  '95', // Foreclosure Sale Continued
  'BE', // Title Issue in Progress
  'BG', // Pre-File Mediation/ Mediation
  // Priority Level 5 (Collections/early delinquency)
  '15', // Short Sale Approved/Marketing Property
  '42', // Delinquent—No Action
  '80', // Breach Letter Sent
  'AW', // Quality Right Party Contact (one month only)
  // Priority Level 6 (Other)
  '26', // Refinance (one month only)
  '49', // Assignment
]);
export type FannieMaeDelinquencyStatusCode = z.infer<typeof FannieMaeDelinquencyStatusCodes>;

export const FannieMaeDelinquencyFileV1 = z.object({
  version: z.literal('1').describe('version'),

  /**
   * ##### Servicer Number
   * Your Fannie Mae–assigned servicer number. Numeric length 9; characters 1 - 9.
   */
  servicerNumber: z.string().describe('string'),

  /**
   * ##### Fannie Mae Loan Number
   * 10-digit unique Fannie Mae loan identifier. Numeric length 10; characters 11 - 20.
   */
  fannieMaeLoanNumber: z.string().describe('string'),

  /**
   * ##### Delinquency Status Code
   * Latest action/stage taken to manage the delinquency for the cycle; report one code per loan per cycle using the Guide's hierarchy.
   * Priority Level 1 (Approved workout options): BF = Trial Modification; 09 = Forbearance; 12 = Repayment Plan; 17 = Short Sale Approved/Offer Received; 27 = Assumption; 28 = Modification; 29 = Charge‑off; 32 = Military Indulgence; 44 = Mortgage Release.
   * Priority Level 2: H5 = Complete Borrower Response Package (one month only).
   * Priority Level 3 (Bankruptcy): 3L = Ch. 7 Asset Case; 3M = Bankruptcy–Property Surrendered; 59 = Ch. 12; 65 = Ch. 7; 66 = Ch. 11; 67 = Ch. 13; 69 = Ch. 13 Plan Confirmed.
   * Priority Level 4 (Foreclosure/legal): 20 = Reinstatement; 24 = Drug Seizure; 30 = Third‑Party Sale; 31 = Probate; 33 = Contested/Litigated Foreclosure; 43 = Foreclosure; 61 = Second Lien Considerations; 71 = Foreclosure Sale Scheduled.
   * Priority Level 5 (Collections/early delinquency): 15 = Short Sale Approved/Marketing Property; 42 = Delinquent—No Action; 80 = Breach Letter Sent; AW = Quality Right Party Contact (one month only).
   * Priority Level 6 (Other): 26 = Refinance (one month only); 49 = Assignment.
   */
  delinquencyStatusCode: FannieMaeDelinquencyStatusCodes.optional().describe('enum'),

  /**
   * ##### Reason for Delinquency Code
   * Primary reason contributing to delinquency. 3-character code.
   */
  reasonForDelinquencyCode: z
    .enum([
      '001', // Death of Borrower
      '002', // Illness of Borrower
      '003', // Illness of Borrower's Family Member
      '004', // Death of Borrower's Family Member
      '005', // Marital Difficulties
      '011', // Property Problem
      '012', // Inability to Sell Property
      '013', // Inability to Rent Property
      '014', // Military Service
      '015', // Other
      '016', // Unemployment
      '017', // Business Failure
      '019', // Casualty Loss
      '023', // Servicing Problems
      '026', // Payment Adjustment
      '027', // Payment Dispute
      '029', // Transfer of Ownership Pending
      '030', // Fraud
      '031', // Unable to Contact Borrower
      'INC', // Incarceration
    ])
    .describe('enum'),

  /**
   * ##### Default Effective Date
   * Date the reported status becomes effective (only for certain codes). Required for codes 09, 12, 15, 17, 80, BF, AW.
   * Format: 8-digit date (YYYYMMDD).
   */
  defaultEffectiveDate: zodDateOrString.optional().describe('date'),

  /**
   * ##### Default Completion Date
   * Date the reported status completed/ended (only for certain codes). Required for codes 09, 12, 15, 17, BF.
   * Format: 8-digit date (YYYYMMDD).
   */
  defaultCompletionDate: zodDateOrString.optional().describe('date'),

  /**
   * ##### Forbearance Program Type Code
   * Indicates which type of forbearance program was used. Required only when reporting forbearance program type.
   * 0 = Forbearance.
   */
  forbearanceProgramTypeCode: z.enum(['0']).optional().describe('enum'),

  /**
   * ##### Imminent Default Indicator
   * Indicates borrower was determined to be in imminent default (applies to certain forbearance types).
   * Required when Forbearance Program Type Code is not 0.
   * 1 = Yes; 0 = No; Space = not applicable.
   */
  imminentDefaultIndicator: z.enum(['1', '0']).optional().describe('enum'),

  /**
   * ##### Forbearance Program Payment Amount
   * Agreed payment amount under the forbearance (not necessarily the amount received).
   * Required when Forbearance Program Type Code is not 0.
   * Format: Number with 2 implied decimals (9(8).99).
   */
  forbearanceProgramPaymentAmount: z.number().optional().describe('currency'),

  /**
   * ##### Forbearance Program Payment Date
   * Date the forbearance payment was received in the last reporting month.
   * Required when reporting forbearance program payment.
   * Format: 8-digit date (YYYYMMDD).
   */
  forbearanceProgramPaymentDate: zodDateOrString.optional().describe('date'),

  /**
   * ##### Ending filler
   */
  endingFiller: z.literal(' '.repeat(9)).describe('filler'),
});
export type FannieMaeDelinquencyFileV1 = z.infer<typeof FannieMaeDelinquencyFileV1>;

export const FannieMaeDelinquencyFile = FannieMaeDelinquencyFileV1;
export type FannieMaeDelinquencyFile = z.infer<typeof FannieMaeDelinquencyFile>;
